import React, { CSSProperties } from 'react'

import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleX,
  IconInfoCircle,
  IconX,
} from '@tabler/icons-react'
import styled, { css } from 'styled-components'

type WrapperProps = {
  bordered?: boolean
}

const BaseAlert = styled.div`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  position: relative;

  ${({ theme: { media } }) => media.sm`
    padding: 0.5rem 1.5rem;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  `}
`

const Title = styled.span`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  white-space: pre-wrap;

  & > svg {
    flex-shrink: 0;
  }
`

const Extra = styled.div`
  flex-shrink: 0;
  max-width: 100%;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colour.neutral.mediumText};

  &:hover {
    color: ${({ theme }) => theme.colour.neutral.darkText};
  }
`

const WarningText = styled(BaseAlert)<WrapperProps>`
  background-color: ${({ theme }) => theme.colour.neutral.surface};
  color: ${({ theme }) => theme.colour.neutral.mediumText};

  ${({ bordered }) =>
    bordered &&
    css`
      border: 1px solid ${({ theme }) => theme.colour.neutral.lightText};
    `}
`

const WarningInfo = styled(BaseAlert)<WrapperProps>`
  background-color: ${({ theme }) => theme.colour.semantic.successLight};
  color: ${({ theme }) => theme.colour.semantic.successDark};

  ${({ bordered }) =>
    bordered &&
    css`
      border: 1px solid ${({ theme }) => theme.colour.semantic.success};
    `}
`

const WarningSuccess = styled(BaseAlert)<WrapperProps>`
  background-color: ${({ theme }) => theme.colour.primary._50};
  color: ${({ theme }) => theme.colour.primary._800};

  ${({ bordered }) =>
    bordered &&
    css`
      border: 1px solid ${({ theme }) => theme.colour.primary._400};
    `}
`

const WarningAlert = styled(BaseAlert)<WrapperProps>`
  background-color: ${({ theme }) => theme.colour.semantic.warningLight};
  color: ${({ theme }) => theme.colour.semantic.warningDark};

  ${({ bordered }) =>
    bordered &&
    css`
      border: 1px solid ${({ theme }) => theme.colour.semantic.warning};
    `}
`

const WarningDanger = styled(BaseAlert)<WrapperProps>`
  background-color: ${({ theme }) => theme.colour.semantic.errorLight};
  color: ${({ theme }) => theme.colour.semantic.errorDark};

  ${({ bordered }) =>
    bordered &&
    css`
      border: 1px solid ${({ theme }) => theme.colour.semantic.error};
    `}
`

export type Props = {
  variant: 'info' | 'warning' | 'success' | 'danger' | 'text'
  title: React.ReactNode
  icon?: React.ReactNode
  bordered?: boolean
  className?: string
  style?: CSSProperties
  children?: React.ReactNode
  closable?: boolean
  onClose?: () => void
}

export type AlertProps = Props

export const Alert: React.FC<Props> = ({
  variant = 'info',
  title,
  icon,
  bordered,
  className,
  style,
  children,
  closable = false,
  onClose,
}) => {
  let Wrapper = WarningInfo
  let defaultIcon = <IconInfoCircle size={16} />

  if (variant === 'warning') {
    Wrapper = WarningAlert
    defaultIcon = <IconAlertTriangle size={16} />
  } else if (variant === 'success') {
    Wrapper = WarningSuccess
    defaultIcon = <IconCircleCheck size={16} />
  } else if (variant === 'danger') {
    Wrapper = WarningDanger
    defaultIcon = <IconCircleX size={16} />
  } else if (variant === 'text') {
    Wrapper = WarningText
    defaultIcon = <IconInfoCircle size={16} />
  }

  return (
    <Wrapper bordered={bordered} className={className} style={style}>
      <Title>
        {icon !== null && (icon || defaultIcon)}
        {title}
      </Title>
      {children && <Extra>{children}</Extra>}
      {closable && (
        <CloseButton onClick={onClose}>
          <IconX size={16} />
        </CloseButton>
      )}
    </Wrapper>
  )
}

Alert.displayName = 'Alert'
